import React, { createContext, useContext } from "react";
import { useGetExpenseRatesForProjectChargeLazyQuery } from "../queries/expenseRates.generated";
import { useGetEffectiveTaskRatesForProjectChargeLazyQuery } from "../queries/taskRates.generated";
import { useGetPerDiemRateForChargeEditorLazyQuery } from "../queries/perDiemRate.generated";
import { useGetEmployeeRateForChargeEditorLazyQuery } from "../queries/employeeRate.generated";
import {
  GetEffectiveExpenseRateSheet,
  GetEffectiveTaskRateSheet,
  GetEmployeeRate,
  GetPerDiemRate,
  RateSheet,
} from "../types";
import { ExpenseRate } from "~/gql/ExpenseRate";
import { PerDiemRate, TaskRate } from "~/gql/types";

type ChargeFormGraphqlApi = {
  getEffectiveExpenseRateSheet: GetEffectiveExpenseRateSheet;
  getEffectiveTaskRateSheet: GetEffectiveTaskRateSheet;
  getPerDiemRate: GetPerDiemRate;
  getEmployeeRate: GetEmployeeRate;
};

export const ChargeFormGraphqlApiContext = createContext<ChargeFormGraphqlApi>(
  null!
);

/**
 * Owns every GraphQL query the charge form runs for form data. Other providers
 * get that data only through `useChargeFormGraphqlApi`, so tests can substitute
 * a mock provider. See the `charge-form-editor-providers` spec.
 */
export const ChargeFormGraphqlProvider: React.FC = ({ children }) => {
  const [getExpenseRateSheet] = useGetExpenseRatesForProjectChargeLazyQuery();
  const [getTaskRateSheet] =
    useGetEffectiveTaskRatesForProjectChargeLazyQuery();
  const [getPerDiemRateQuery] = useGetPerDiemRateForChargeEditorLazyQuery();
  const [getEmployeeRateQuery] = useGetEmployeeRateForChargeEditorLazyQuery();

  const getEffectiveExpenseRateSheet = async (
    asOf: string,
    projectNumber: number
  ): Promise<RateSheet | null> => {
    const variables = { asOf, projectNumber };
    const result = await getExpenseRateSheet({ variables });
    const rateSheet = result.data?.projects?.effectiveExpenseRateSheet;

    if (!rateSheet) {
      return null;
    }

    return {
      name: rateSheet.name,
      rates: rateSheet.expenseRates as ExpenseRate[],
    };
  };

  const getEffectiveTaskRateSheet = async (
    asOf: string,
    projectNumber: number
  ): Promise<RateSheet | null> => {
    const variables = { asOf, projectNumber };
    const result = await getTaskRateSheet({ variables });
    const rateSheet = result.data?.projects?.effectiveTaskRateSheet;

    if (!rateSheet) {
      return null;
    }

    return {
      name: rateSheet.name,
      rates: rateSheet.rates as TaskRate[],
    };
  };

  const getPerDiemRate = async (
    effectiveDate: string,
    state: string,
    county: string
  ): Promise<PerDiemRate | null> => {
    const variables = { effectiveDate, state, county };
    const result = await getPerDiemRateQuery({ variables });
    return (result.data?.perDiemRates?.rate as PerDiemRate) ?? null;
  };

  const getEmployeeRate = async (
    userPrincipalName: string,
    effectiveDate: string
  ): Promise<number> => {
    const variables = { userPrincipalName, effectiveDate };
    const result = await getEmployeeRateQuery({ variables });
    return result.data?.employeeRates?.single?.rate ?? 0;
  };

  return (
    <ChargeFormGraphqlApiContext.Provider
      value={{
        getEffectiveExpenseRateSheet: getEffectiveExpenseRateSheet,
        getEffectiveTaskRateSheet: getEffectiveTaskRateSheet,
        getPerDiemRate: getPerDiemRate,
        getEmployeeRate: getEmployeeRate,
      }}
    >
      {children}
    </ChargeFormGraphqlApiContext.Provider>
  );
};

const useProviderContext = (fn: string): ChargeFormGraphqlApi => {
  const context = useContext(ChargeFormGraphqlApiContext);

  if (!context) {
    throw new Error(`Cannot call ${fn} outside of a ChargeFormGraphqlProvider`);
  }

  return context;
};

export function useChargeFormGraphqlApi(): ChargeFormGraphqlApi {
  const data = useProviderContext("useChargeFormGraphqlApi");

  return data;
}
